import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { PerformanceKpiResponse } from "@/actions/ssr/performance/getPerformanceKpiSSR";
import type { TopAttendeesResponse } from "@/actions/ssr/performance/getTopAttendeesSSR";

interface PerformanceState {
  kpi: PerformanceKpiResponse;
  topAttendees: TopAttendeesResponse;
  performanceDateFilter: { from: string | null; to: string | null };
  kpiClientFetched: boolean;
  topAttendeesClientFetched: boolean;
}

const KPI_FALLBACK: PerformanceKpiResponse = {
  top_of_month: null,
  top_of_week: null,
  top_department: null,
  top_target_achievers: null,
  top_attendees: null,
  sales_per_staff: null,
};

const TOP_ATTENDEES_FALLBACK: TopAttendeesResponse = {
  groups: [
    { attendance_pct: 100, staff_count: 0 },
    { attendance_pct: 97, staff_count: 0 },
    { attendance_pct: 95, staff_count: 0 },
    { attendance_pct: 94, staff_count: 0 },
    { attendance_pct: 92, staff_count: 0 },
    { attendance_pct: 90, staff_count: 0 },
  ],
};

const initialState: PerformanceState = {
  kpi: KPI_FALLBACK,
  topAttendees: TOP_ATTENDEES_FALLBACK,
  performanceDateFilter: { from: null, to: null },
  kpiClientFetched: false,
  topAttendeesClientFetched: false,
};

const performanceSlice = createSlice({
  name: "performance",
  initialState,
  reducers: {
    setPerformanceKpi(state, action: PayloadAction<PerformanceKpiResponse>) {
      state.kpiClientFetched = true;
      state.kpi = action.payload;
    },
    setPerformanceKpiFromSSR(
      state,
      action: PayloadAction<PerformanceKpiResponse>,
    ) {
      if (state.kpiClientFetched) return;
      state.kpi = action.payload;
    },
    setTopAttendees(state, action: PayloadAction<TopAttendeesResponse>) {
      state.topAttendeesClientFetched = true;
      state.topAttendees = action.payload;
    },
    setTopAttendeesFromSSR(state, action: PayloadAction<TopAttendeesResponse>) {
      if (state.topAttendeesClientFetched) return;
      state.topAttendees = action.payload;
    },
    setPerformanceDateFilter(
      state,
      action: PayloadAction<{ from: string | null; to: string | null }>,
    ) {
      state.performanceDateFilter = action.payload;
    },
  },
});

export const {
  setPerformanceKpi,
  setPerformanceKpiFromSSR,
  setTopAttendees,
  setTopAttendeesFromSSR,
  setPerformanceDateFilter,
} = performanceSlice.actions;
export default performanceSlice.reducer;
